import dayjs from "dayjs";
import { services } from "@/utils/config/services";
import { customFetch, customFetchDownload } from "../customFetch";
import { Sorter } from "../sorter";
import { filterDateFormat } from "../constants";

export interface IncidentsExportFilter {
  companyId?: string;
  endpointId?: string;
  sha256?: string;
  aggerVersion?: string;
  reason?: string;
  isAllowed?: boolean;
  isMitigated?: boolean;
  isBaselineModeEnabled?: boolean;
  detectedFrom?: string;
  detectedTo?: string;
}

export interface IncidentsFilters {
  aggerVersion: string;
  status: string;
  reason: string;
  from: string;
  to: string;
}

export enum IncidentsEntities {
  endpoint = "endpoint",
  hash = "hash",
}

function formatEventDateFilter(from: string, to: string) {
  const formattedFrom = dayjs(from, filterDateFormat)
    .set("h", 0)
    .set("m", 0)
    .set("s", 0)
    .set("ms", 0)
    .toISOString();
  const formattedTo = dayjs(to, filterDateFormat)
    .set("h", 23)
    .set("m", 59)
    .set("s", 59)
    .set("ms", 999)
    .toISOString();
  return [formattedFrom, formattedTo];
}

const getStatusFilterQuery = (value: string | null) => {
  switch (value) {
    case "Whitelisted":
      return `&IsAllowed=true`;
    case "Mitigated":
      return `&IsMitigated=true&IsAllowed=false`;
    case "Unmitigated":
      return `&IsMitigated=false&IsAllowed=false&IsBaselineModeEnabled=false`;
    case "Baselined":
      return `&IsBaselineModeEnabled=true&IsAllowed=false`;
    default:
      return "";
  }
};

const getStatusFilterBody = (value: string | null): IncidentsExportFilter => {
  switch (value) {
    case "Whitelisted":
      return { isAllowed: true };
    case "Mitigated":
      return { isMitigated: true, isAllowed: false };
    case "Unmitigated":
      return {
        isMitigated: false,
        isAllowed: false,
        isBaselineModeEnabled: false,
      };
    case "Baselined":
      return { isBaselineModeEnabled: true, isAllowed: false };
    default:
      return {};
  }
};

export const buildIncidentsExportFilter = (
  filters: IncidentsFilters,
  companyId?: string,
): IncidentsExportFilter => {
  const filter: IncidentsExportFilter = {
    companyId,
    ...getStatusFilterBody(filters.status),
  };

  if (filters.aggerVersion !== "All") {
    filter.aggerVersion = filters.aggerVersion;
  }

  if (filters.reason !== "All") {
    filter.reason = filters.reason;
  }

  if (filters.from && filters.to) {
    const [from, to] = formatEventDateFilter(filters.from, filters.to);
    filter.detectedFrom = from;
    filter.detectedTo = to;
  }

  return filter;
};

export const incidents = {
  getIncident: (id: string) => customFetch(`${services.incidents}/${id}`),
  getIncidentsByEntity: (
    page: number,
    id: string,
    entity: IncidentsEntities,
    companyId: string | undefined,
    filters: IncidentsFilters,
    sorter?: Sorter,
  ) => {
    let url = `${services.incidents}?PageNumber=${page}&PageSize=20`;
    switch (entity) {
      case IncidentsEntities.endpoint:
        url += `&EndpointId=${id}`;
        break;
      case IncidentsEntities.hash:
        url += `&Sha256=${id}`;
        break;
    }
    if (companyId) {
      url += `&CompanyId=${companyId}`;
    }
    if (filters.aggerVersion !== "All") {
      url += `&AggerVersion=${filters.aggerVersion}`;
    }
    if (filters.status !== "All") {
      url += getStatusFilterQuery(filters.status);
    }
    if (filters.reason !== "All") {
      url += `&Reason=${filters.reason}`;
    }
    if (filters.from && filters.to) {
      const [from, to] = formatEventDateFilter(filters.from, filters.to);
      url += `&DetectedFrom=${from}&DetectedTo=${to}`;
    }
    if (sorter) {
      url += `&OrderBy=${sorter.orderBy}&OrderDirection=${sorter.orderDirection}`;
    }
    return customFetch(url);
  },
  getIncidents: (
    page: number,
    pageSize: number,
    companyId: string,
    filters: IncidentsFilters,
    sorter?: Sorter,
  ) => {
    let url = `${services.incidents}?CompanyId=${companyId}&PageNumber=${page}&PageSize=${pageSize}`;
    if (filters.aggerVersion !== "All") {
      url += `&AggerVersion=${filters.aggerVersion}`;
    }
    if (filters.status !== "All") {
      url += getStatusFilterQuery(filters.status);
    }
    if (filters.reason !== "All") {
      url += `&Reason=${filters.reason}`;
    }
    if (filters.from && filters.to) {
      const [from, to] = formatEventDateFilter(filters.from, filters.to);
      url += `&DetectedFrom=${from}&DetectedTo=${to}`;
    }
    if (sorter) {
      url += `&OrderBy=${sorter.orderBy}&OrderDirection=${sorter.orderDirection}`;
    }
    return customFetch(url);
  },
  allowIncident: (id: string) =>
    customFetch(`${services.incidents}/${id}/allow`, { method: "PUT" }),
  blockIncident: (id: string) =>
    customFetch(`${services.incidents}/${id}/block`, { method: "PUT" }),
  restoreIncident: (id: string) =>
    customFetch(`${services.incidents}/${id}/restore`, { method: "POST" }),
  cancelRestoreIncident: (id: string) =>
    customFetch(`${services.incidents}/${id}/cancel-restore`, {
      method: "POST",
    }),
  deleteQuarantinedIncident: (id: string) =>
    customFetch(`${services.incidents}/${id}/delete-quarantine`, {
      method: "POST",
    }),
  cancelDeleteQuarantinedIncident: (id: string) =>
    customFetch(`${services.incidents}/${id}/cancel-delete-quarantine`, {
      method: "POST",
    }),
  deleteDump: (id: string) =>
    customFetch(`${services.incidents}/${id}/delete-dump`, {
      method: "POST",
    }),
  cancelDeleteDump: (id: string) =>
    customFetch(`${services.incidents}/${id}/cancel-delete-dump`, {
      method: "POST",
    }),
  getIncidentsAggerVersions: (companyId: string, filters: IncidentsFilters) => {
    let url = `${services.incidents}/versions?CompanyId=${companyId}`;
    if (filters.status !== "All") {
      url += getStatusFilterQuery(filters.status);
    }
    if (filters.reason !== "All") {
      url += `&Reason=${filters.reason}`;
    }
    if (filters.from && filters.to) {
      const [from, to] = formatEventDateFilter(filters.from, filters.to);
      url += `&DetectedFrom=${from}&DetectedTo=${to}`;
    }
    return customFetch(url);
  },
  getIncidentsAggerVersionsByEntity: (
    id: string,
    entity: IncidentsEntities,
    filters: IncidentsFilters,
  ) => {
    let url = `${services.incidents}/versions?`;
    switch (entity) {
      case IncidentsEntities.endpoint:
        url += `EndpointId=${id}`;
        break;
      case IncidentsEntities.hash:
        url += `Sha256=${id}`;
        break;
    }
    if (filters.status !== "All") {
      url += getStatusFilterQuery(filters.status);
    }
    if (filters.reason !== "All") {
      url += `&Reason=${filters.reason}`;
    }
    if (filters.from && filters.to) {
      const [from, to] = formatEventDateFilter(filters.from, filters.to);
      url += `&DetectedFrom=${from}&DetectedTo=${to}`;
    }
    return customFetch(url);
  },
  exportIncident: (id: string) =>
    customFetchDownload(
      `${services.incidents}/${id}/export`,
      `incident-${id}.json`,
    ),
  exportIncidents: (ids: string[], filter?: IncidentsExportFilter) =>
    customFetchDownload(`${services.incidents}/export`, "incidents.json", {
      method: "POST",
      body: JSON.stringify({ ids, filter }),
    }),
  getZipPassword: (id: string) =>
    customFetch(`${services.incidents}/${id}/zip-password`),
  removeFirewallRuleIncident: (id: string) =>
    customFetch(`${services.incidents}/${id}/remove-firewall-rule`, {
      method: "POST",
    }),
  cancelRemoveFirewallRuleIncident: (id: string) =>
    customFetch(`${services.incidents}/${id}/cancel-remove-firewall-rule`, {
      method: "POST",
    }),
};
